import {
  activePharmacistLists,
  adminActions,
  adminDeactivatedPharmacists,
  adminDeletedPharmacists,
  adminHiddenPharmacists,
  adminPharmacistSearchedDetails,
  getActivePharmacistList,
  getDeactivatedPharmacists,
  getDeletedPharmacist,
  getHiddenPharmacists,
  ProfessionalOnlyUsers,
  successSelector,
  TypeOfUser,
} from '@pharmaplan/common';
import { UserListTabTypes } from '../../helpers/Constants';
import { useAppSelector } from '../useAppSelector';
import strings from '../../localization';

const { pharmacist, student, technician } = TypeOfUser;
const { active, deactivated, deleted, hidden, detailsSearch } = UserListTabTypes;
const {
  getActivePharmacistList: activePharmacistAction,
  getDeactivatedPharmacists: deactivePharmacistAction,
  getHiddenPharmacists: hiddenPharmacistAction,
  getDeletedPharmacist: deletedPharmacistAction,
  getPharmacistDetailsSearchFilters,
} = adminActions;

const useProfessionalListConfig = (
  userType: ProfessionalOnlyUsers,
) => {
  const config = {
    [pharmacist]: {
      userLabel: strings.pharmacists,
      selectors: {
        [active]: activePharmacistLists,
        [deactivated]: adminDeactivatedPharmacists,
        [hidden]: adminHiddenPharmacists,
        [deleted]: adminDeletedPharmacists,
        [detailsSearch]: adminPharmacistSearchedDetails,
      },
      successActions: {
        [active]: activePharmacistAction,
        [deactivated]: deactivePharmacistAction,
        [hidden]: hiddenPharmacistAction,
        [deleted]: deletedPharmacistAction,
        [detailsSearch]: getPharmacistDetailsSearchFilters,
      },
      api: {
        [active]: getActivePharmacistList,
        [deactivated]: getDeactivatedPharmacists,
        [hidden]: getHiddenPharmacists,
        [deleted]: getDeletedPharmacist,
      },
    },
    [student]: {
      userLabel: strings.pharmacyStudent,
      selectors: {
        [active]: activePharmacistLists,
        [deactivated]: adminDeactivatedPharmacists,
        [hidden]: adminHiddenPharmacists,
        [deleted]: adminDeletedPharmacists,
        [detailsSearch]: adminPharmacistSearchedDetails,
      },
      successActions: {
        [active]: activePharmacistAction,
        [deactivated]: deactivePharmacistAction,
        [hidden]: hiddenPharmacistAction,
        [deleted]: deletedPharmacistAction,
        [detailsSearch]: getPharmacistDetailsSearchFilters,
      },
      api: {
        [active]: getActivePharmacistList,
        [deactivated]: getDeactivatedPharmacists,
        [hidden]: getHiddenPharmacists,
        [deleted]: getDeletedPharmacist,
      },
    },
    [technician]: {
      userLabel: strings.technicians,
      selectors: {
        [active]: activePharmacistLists,
        [deactivated]: adminDeactivatedPharmacists,
        [hidden]: adminHiddenPharmacists,
        [deleted]: adminDeletedPharmacists,
        [detailsSearch]: adminPharmacistSearchedDetails,
      },
      successActions: {
        [active]: activePharmacistAction,
        [deactivated]: deactivePharmacistAction,
        [hidden]: hiddenPharmacistAction,
        [deleted]: deletedPharmacistAction,
        [detailsSearch]: getPharmacistDetailsSearchFilters,
      },
      api: {
        [active]: getActivePharmacistList,
        [deactivated]: getDeactivatedPharmacists,
        [hidden]: getHiddenPharmacists,
        [deleted]: getDeletedPharmacist,
      },
    },
  };

  const userConfig = config[userType] ?? {};
  const { selectors, successActions } = userConfig;

  const {
    active: activeSelect,
    deactivated: deactiveSelect,
    hidden: hiddenSelect,
    detailsSearch: detailsSelect,
    deleted: deletedSelect,
  } = selectors ?? {};

  const {
    active: activeSuccessAction,
    deactivated: deactivatedSuccessAction,
    hidden: hiddenSuccessAction,
    deleted: deletedSuccessAction,
    detailsSearch: detailsSuccessAction,
  } = successActions ?? {};

  const activeSuccess = useAppSelector((state) =>
    successSelector([activeSuccessAction], state));
  const deactiveSuccess = useAppSelector((state) =>
    successSelector([deactivatedSuccessAction], state));
  const hiddenSuccess = useAppSelector((state) =>
    successSelector([hiddenSuccessAction], state));
  const deletedSuccess = useAppSelector((state) =>
    successSelector([deletedSuccessAction], state));
  const detailsSuccess = useAppSelector((state) =>
    successSelector([detailsSuccessAction], state));

  const activeProfessionals = useAppSelector(activeSelect);
  const deactivatedProfessionals = useAppSelector(deactiveSelect);
  const hiddenProfessionals = useAppSelector(hiddenSelect);
  const deletedProfessionals = useAppSelector(deletedSelect);
  const details = useAppSelector(detailsSelect);

  const dataConfig = {
    [active]: activeProfessionals,
    [deactivated]: deactivatedProfessionals,
    [hidden]: hiddenProfessionals,
    [deleted]: deletedProfessionals,
    [detailsSearch]: details,
  };

  const successes = {
    [active]: activeSuccess,
    [deactivated]: deactiveSuccess,
    [hidden]: hiddenSuccess,
    [deleted]: deletedSuccess,
    [detailsSearch]: detailsSuccess,
  };

  return { ...config[userType], dataConfig, successes };
};

export default useProfessionalListConfig;
